// Client-side image compression.
//
// Re-encodes a JPEG at reduced quality at its ORIGINAL pixel dimensions, so the
// file gets lighter (KB/MB) without changing the photo's width, height, or
// shape. Drawing through a canvas also drops EXIF/other metadata for free.
//
// Only JPEGs are compressed for now — every other type (PNG, GIF, SVG, PDF,
// text, …) is returned untouched so we never change a file's format or break
// transparency/animation. WebP conversion can be layered on later if needed.
//
// Safe by design: if anything fails, or the result isn't actually smaller, the
// original file is returned, so an upload is never blocked or made worse.

const JPEG_QUALITY = 0.8;

export async function compressImageFile(file: File): Promise<File> {
  if (file.type !== "image/jpeg") return file;

  try {
    // `imageOrientation: "from-image"` bakes EXIF rotation into the pixels, so a
    // portrait phone photo doesn't come back sideways once the metadata is gone.
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", JPEG_QUALITY);
    });

    // Keep the original when re-encoding didn't actually help (e.g. an image
    // that is already well-compressed) so we never upload a larger file.
    if (!blob || blob.size >= file.size) return file;

    return new File([blob], file.name, {
      type: "image/jpeg",
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}
